import { sectionRenderer, normalizeSectionId } from '@theme/section-renderer';

/**
 * <recipe-filter-bar> wraps the Waterloo Flavor / Beverage Type (or Event/Category)
 * filter pills used on the Recipe and News/Events blog grids.
 *
 * Two modes, set via [data-mode]:
 *  - "tag" (default): links are native Shopify /tagged/{tag} (or base blog) routes.
 *    Clicks are intercepted and re-rendered in place via the Section Rendering API
 *    (same mechanism the PLP facets use) instead of a full page navigation, so the
 *    grid filters without leaving the page. The URL still updates via pushState for
 *    shareable/bookmarkable links and back/forward support.
 *  - "metaobject": for filtering by a recipe's Product/Type metaobject fields instead
 *    of tags (blogs have no server-side metafield filtering). Links carry
 *    data-filter-param/data-filter-value; clicks update the URL's query string and
 *    dispatch a `recipe-filter:change` event for <recipe-grid> to filter
 *    already-rendered cards client-side by their [data-recipe-flavor]/
 *    [data-recipe-types] attributes.
 *
 * Both modes close any open dropdown on an outside click, and close the dropdown
 * after a filter is applied.
 */
class RecipeFilterBar extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.mode || 'tag';

    this.querySelectorAll('.recipe-filter-pill').forEach((pill) => {
      const label = pill.querySelector('.recipe-filter-pill__summary > span:first-child');
      if (label) pill.dataset.defaultLabel = label.textContent;
    });

    this._handleOutsideClick = this._handleOutsideClick.bind(this);
    document.addEventListener('click', this._handleOutsideClick);

    this._handleClick = this._handleClick.bind(this);
    this.addEventListener('click', this._handleClick);

    this._handleAvailableOptions = this._handleAvailableOptions.bind(this);
    document.addEventListener('recipe-grid:options-update', this._handleAvailableOptions);

    if (this.mode === 'metaobject') {
      this._syncActiveStates();
      this._dispatchChange();
    }

    this._updateSummaryLabels();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._handleOutsideClick);
    this.removeEventListener('click', this._handleClick);
    document.removeEventListener('recipe-grid:options-update', this._handleAvailableOptions);
  }

  /**
   * Hides options that would return zero matches given the *other* filter(s) currently
   * applied — e.g. picking a Flavor narrows the Type dropdown to types that flavor
   * actually has, and vice versa. "All" always stays visible.
   * @param {CustomEvent<Record<string, string[]>>} event
   */
  _handleAvailableOptions(event) {
    const available = event.detail || {};

    this.querySelectorAll('a[data-filter-param]').forEach((link) => {
      const param = link.dataset.filterParam;
      const value = link.dataset.filterValue || '';
      const li = link.closest('li');
      if (!li) return;

      if (!value || !Object.prototype.hasOwnProperty.call(available, param)) {
        li.hidden = false;
        return;
      }

      li.hidden = !available[param].includes(value);
    });
  }

  _handleOutsideClick(event) {
    this.querySelectorAll('details[open]').forEach((details) => {
      if (!details.contains(event.target)) {
        details.removeAttribute('open');
      }
    });
  }

  _handleClick(event) {
    const link = event.target instanceof Element ? event.target.closest('a') : null;
    if (!link || !this.contains(link)) return;

    if (this.mode === 'metaobject' && link.hasAttribute('data-filter-param')) {
      this._handleMetaobjectFilterClick(event, link);
    } else if (this.mode === 'tag') {
      this._handleTagFilterClick(event, link);
    }
  }

  _handleMetaobjectFilterClick(event, link) {
    event.preventDefault();
    const param = link.dataset.filterParam;
    const value = link.dataset.filterValue || '';
    const url = new URL(window.location.href);

    if (value) {
      url.searchParams.set(param, value);
    } else {
      url.searchParams.delete(param);
    }

    window.history.replaceState({}, '', url);
    this._syncActiveStates();
    this._updateSummaryLabels();
    this._dispatchChange();

    const details = link.closest('details');
    if (details) details.removeAttribute('open');
  }

  async _handleTagFilterClick(event, link) {
    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return; // let the native link navigate as a fallback

    event.preventDefault();

    const sectionId = normalizeSectionId(sectionElement.id);
    const url = new URL(link.href, window.location.origin);

    try {
      await sectionRenderer.renderSection(sectionId, { url });
      window.history.pushState({}, '', url);
    } catch (error) {
      window.location.href = link.href;
      return;
    }

    // The morphed-in markup is server-rendered, so its summary labels are back to
    // the plain defaults and its `.recipe-filter-pill` nodes may be fresh instances —
    // recapture the defaults and reapply the "currently selected" label on top of them.
    this.querySelectorAll('.recipe-filter-pill').forEach((pill) => {
      const label = pill.querySelector('.recipe-filter-pill__summary > span:first-child');
      if (label) pill.dataset.defaultLabel = label.textContent;
    });
    this._updateSummaryLabels();

    const details = link.closest('details');
    if (details) details.removeAttribute('open');
  }

  /**
   * Mirrors Liquid's `handle` filter closely enough to compare a filter value against
   * the tag segment of a /tagged/{handle} URL (lowercased, non-alphanumerics collapsed
   * to single hyphens, trimmed).
   * @param {string} value
   */
  static _toHandle(value) {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  _syncActiveStates() {
    const params = new URL(window.location.href).searchParams;

    // "type"/"flavor" pills in "metaobject" mode filter via query string on this page,
    // but a shopper can also land here via a real Shopify /tagged/{handle} URL (eg. an
    // article's own tag link) — recognize that as the active selection too, not just
    // the query string.
    const taggedMatch = window.location.pathname.match(/\/tagged\/([^/]+)/);
    const urlTagHandle = taggedMatch ? decodeURIComponent(taggedMatch[1]).toLowerCase() : '';

    this.querySelectorAll('a[data-filter-param]').forEach((link) => {
      const current = params.get(link.dataset.filterParam) || '';
      const value = link.dataset.filterValue || '';
      const isActive = value
        ? current === value || (!current && urlTagHandle && urlTagHandle === RecipeFilterBar._toHandle(value))
        : !current && !urlTagHandle;

      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Shows the currently selected option's text in each pill's summary in place of
   * the default label (e.g. "Ruby Red Tangerine" instead of "Waterloo Flavor"), and
   * reverts to the default label when nothing is selected ("All").
   */
  _updateSummaryLabels() {
    this.querySelectorAll('.recipe-filter-pill').forEach((pill) => {
      const label = pill.querySelector('.recipe-filter-pill__summary > span:first-child');
      if (!label) return;

      const defaultLabel = pill.dataset.defaultLabel ?? label.textContent;
      const selected = pill.querySelector('.recipe-filter-pill__list a[aria-current="true"]');
      const selectedText = selected ? selected.textContent.trim() : '';
      const hasSelection = Boolean(selectedText && selectedText !== 'All');

      label.textContent = hasSelection ? selectedText : defaultLabel;
      pill.classList.toggle('recipe-filter-pill--active', hasSelection);
    });
  }

  _dispatchChange() {
    const params = new URL(window.location.href).searchParams;
    this.dispatchEvent(
      new CustomEvent('recipe-filter:change', {
        bubbles: true,
        detail: Object.fromEntries(params.entries()),
      })
    );
  }
}

if (!customElements.get('recipe-filter-bar')) {
  customElements.define('recipe-filter-bar', RecipeFilterBar);
}
